import {
    StreamingResponseErrorDetail,
    StreamingResponseMessagePosition,
    TradingAlgorithmBacktestRequest,
    TradingAlgorithmBacktestStreamingResponse,
    TradingBacktestResult
} from "../utils/typesTrading";
import {getJunkyardWebsocketHostname} from "../utils/navigation";
import type {PyProxy} from "pyodide/ffi";
import {
    BacktestCancelHandle,
    ClientPositionChange,
    ClientPositionChanges,
    MessageType,
    ServerBacktestIntervalRequest,
    StreamingData
} from "../utils/typesTradingWs";
import {safeRecv, safeSend} from "../utils/websocketUtils"
import {getAlgoInstance, getPyodide} from "../utils/pyodideUtils";


function openWebSocket(url: string, subprotocols: string[]): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
        const ws = new WebSocket(url, subprotocols);
        ws.binaryType = "arraybuffer"; // so onmessage gives ArrayBuffer, not Blob
        ws.onopen = () => resolve(ws);
        ws.onerror = () => reject(new Error(`WebSocket connection to ${url} failed`));
    });
}


function isMessageType(msg: StreamingData, type: MessageType): boolean {
    return msg.message_type === type;
}


const getClientPositionChanges = async (
    ws: WebSocket,
    pythonAlgoProxy: PyProxy,
    currentDateStr: string,

): Promise<[ClientPositionChanges | null, StreamingResponseErrorDetail | null]> => {
    /*
    1. Hook into pyodide algo's current state
    2. Run current date logic
    3. Send price requests first (if any)
    4. Send position changes after applying algo logic
     */
    const pyodide = await getPyodide();
    const pyDate = pyodide.pyimport("datetime").date.fromisoformat(currentDateStr);

    try {
        const resultProxy = pythonAlgoProxy.exec_daily.callKwargs({current_date: pyDate});
        const errorDetail = {
            exception: resultProxy.error ?? null,
            traceback: resultProxy.traceback ?? null,
        } as StreamingResponseErrorDetail;
        if (errorDetail.exception) {
            return [null, errorDetail];
        }
        // const rawChanges = resultProxy.toJs({
        //     dict_converter: Object.fromEntries,
        //     create_proxies: false,
        // }) as ClientPositionChange[];
        const rawResult = resultProxy.toJs() as Map<string, any>[];

        const changes: ClientPositionChange[] = rawResult.map((entry) => {
            return {
                trade_date: entry.get("trade_date"),
                ticker: entry.get("ticker"),
                price: entry.get("price"),
                action: entry.get("action"),
                shares: entry.get("shares"),
            };
        });

        // console.info("Conversion rawChanges", rawChanges);
        const result = {"position_changes": changes} as ClientPositionChanges;
        console.info("Position change request", result);
        resultProxy.destroy();
        return [result, null];
    } catch (e) {
        return [null, {exception: "Error", "traceback": "No traceback"}];
    }
}

export function makeBacktestCancelHandle(): BacktestCancelHandle {
    return {
        cancelled: false,
        close: () => {
            // no-op placeholder; replaced in backtestTradingAlgorithmWs
            // once the WebSocket exists, so it's safe to call early
        },
    };
}

export async function* backtestTradingAlgorithmWs(
    props: TradingAlgorithmBacktestRequest,
    closeHandler: BacktestCancelHandle
    ): AsyncGenerator<
    TradingAlgorithmBacktestStreamingResponse |
    TradingBacktestResult |
    StreamingResponseErrorDetail |
    StreamingResponseMessagePosition,
    void,
    unknown> {
    // TODO: expose way to close the socket connection when user navigates away

    const hostname = getJunkyardWebsocketHostname();
    const queryParams = new URLSearchParams({
        access_token: props.access_token,
        start_date: props.start_date,
        end_date: props.end_date,
        ticker_universe: props.tickers_universe,
        starting_cash: String(props.starting_cash),
        strategy_name: props.trading_algo.strategy_name,
    });
    const url = `${hostname}/tradingws/v1/backtest-sessions?${queryParams.toString()}`;

    let ws = await openWebSocket(url, ["msgpack"]);
    console.debug("Got here ws...")
    closeHandler.close = () => {
        ws.close();
        closeHandler.cancelled = true;
    }

    try {

        const msgHello = await safeRecv(ws);
        const sessionId = msgHello.session_id;
        const pyAlgoProxy: PyProxy = await getAlgoInstance(
            ws,
            sessionId,
            props.trading_algo,
            props.start_date,
            props.end_date,
            props.starting_cash,
            props.tickers_universe
        );
        if (pyAlgoProxy.exception) {
            const errorDetail = {
                exception: pyAlgoProxy.error ?? "",
                traceback: pyAlgoProxy.traceback ?? "",
            } as StreamingResponseErrorDetail;
            console.error(`Failed to setup algo instance: ${errorDetail.exception}`)
            yield errorDetail;
            return;
        }
        console.debug("Created algorithm...")

        // Hello handshake
        if (!isMessageType(msgHello, MessageType.Hello)) {
            throw new Error("Unexpected protocol. No hello message received!");
        }
        safeSend(ws, msgHello);

        const msgBtBegin = await safeRecv(ws);
        if (!isMessageType(msgBtBegin, MessageType.BacktestBegin)) {
            throw new Error("Unexpected protocol. No backtest begin message received!");
        }

        let currentDate = props.start_date;
        let stopReceived = false;

        while (currentDate < props.end_date) {
            const envelope = await safeRecv(ws);

            if (isMessageType(envelope, MessageType.BacktestStop)) {
                stopReceived = true;
                break;
            }

            if (!isMessageType(envelope, MessageType.BacktestInterval)) {
                throw new Error("Unexpected protocol. No backtest interval message received!");
            }

            const intervalData = envelope.message_data as ServerBacktestIntervalRequest | undefined;
            if (!intervalData?.current_date) {
                throw new Error("Unexpected protocol. No backtest interval message received!");
            }
            currentDate = intervalData.current_date;
            console.debug(`Current date ${currentDate}`);

            // TODO: Get client side position changes
            // TODO: Client side position changes can also request ticker prices for the
            //       current date from the server
            const [positionChanges, errorDetail] = await getClientPositionChanges(ws, pyAlgoProxy, currentDate);
            if (errorDetail) {
                yield errorDetail;
            }

            const posChangeReq = {
                message_type: MessageType.ClientPositionChangeRequest,
                session_id: sessionId,
                message_data: {"position_changes": positionChanges?.position_changes || []}
            } as unknown as StreamingData;

            safeSend(ws, posChangeReq);
            const posChangeResp= await safeRecv(ws);

            // TODO: Optionally receive snapshot for current date
            yield posChangeResp.message_data as TradingAlgorithmBacktestStreamingResponse;

        }

        if (!stopReceived) {
            const msgStop = await safeRecv(ws);
            if (!isMessageType(msgStop, MessageType.BacktestStop)) {
                throw new Error("Unexpected protocol. Expected backtest_stop message before result!");
            }
        }

        const msgResult = await safeRecv(ws);
        const result = msgResult.message_data as TradingBacktestResult | undefined;
        if (!result?.printout) {
            throw new Error(`Invalid backtest result: ${typeof msgResult.message_data}`);
        }
        yield result as TradingBacktestResult;
    } catch (error) {
        const err = {
            exception: String(error),
            traceback: "",
        } as StreamingResponseErrorDetail;
        console.error(`Failed to run algorithm: ${err}`)
        yield err;
    }
    finally {
        ws.close();
    }
}
